import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  WhatsApp,
  CheckCircle,
  Error,
  Send,
  Refresh,
} from "@mui/icons-material";

interface VerificationStatus {
  has_whatsapp_number: boolean;
  whatsapp_number: string | null;
  is_verified: boolean;
  verified_at: string | null;
}

const WhatsAppVerification: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Load verification status on component mount
  useEffect(() => {
    loadVerificationStatus();
  }, []);

  const loadVerificationStatus = async () => {
    try {
      const response = await fetch("/api/whatsapp-verification/status", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(data.data);

        // Set active step based on status
        if (!data.data.has_whatsapp_number) {
          setActiveStep(0); // Add phone number
        } else if (!data.data.is_verified) {
          setActiveStep(1); // Verify code
        } else {
          setActiveStep(2); // Completed
        }
      } else if (response.status === 401) {
        setError("Authentication failed. Please log in again.");
      }
    } catch (error) {
      console.error("Error loading verification status:", error);
    }
  };

  const handleAddPhoneNumber = async () => {
    if (!phoneNumber.trim()) {
      setError("Please enter a phone number");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/whatsapp-verification/add-number", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ phone_number: phoneNumber.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setActiveStep(1);
        setVerificationCode("");
      } else {
        if (response.status === 401) {
          setError("Authentication failed. Please log in again.");
        } else {
          setError(data.detail || "Failed to add phone number");
        }
      }
    } catch (error) {
      console.error("Error adding phone number:", error);
      setError("Failed to add phone number. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError("Please enter the verification code");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/whatsapp-verification/verify-code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          verification_code: verificationCode.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setActiveStep(2);
        await loadVerificationStatus(); // Refresh status
      } else {
        setError(data.detail || "Invalid verification code");
      }
    } catch (error) {
      console.error("Error verifying code:", error);
      setError("Failed to verify code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/whatsapp-verification/resend-code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ phone_number: phoneNumber }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
      } else {
        setError(data.detail || "Failed to resend code");
      }
    } catch (error) {
      console.error("Error resending code:", error);
      setError("Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    "Add WhatsApp Number",
    "Verify Phone Number",
    "Verification Complete",
  ];

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <WhatsApp color="primary" />
        WhatsApp Verification
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Status Display */}
        {status && (
          <Card
            sx={{ mb: 3, bgcolor: status.is_verified ? "#e8f5e8" : "#fff3cd" }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Status
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                {status.is_verified ? (
                  <CheckCircle color="success" />
                ) : (
                  <Error color="warning" />
                )}
                <Typography>
                  {status.is_verified
                    ? "WhatsApp number verified"
                    : status.has_whatsapp_number
                    ? "WhatsApp number not verified"
                    : "No WhatsApp number added"}
                </Typography>
              </Box>
              {status.whatsapp_number && (
                <Typography variant="body2" color="text.secondary">
                  Phone: {status.whatsapp_number}
                </Typography>
              )}
              {status.verified_at && (
                <Typography variant="body2" color="text.secondary">
                  Verified: {new Date(status.verified_at).toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 0: Add Phone Number */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Add Your WhatsApp Number
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your WhatsApp phone number to receive important
              notifications about your auctions.
            </Typography>

            <TextField
              fullWidth
              label="WhatsApp Phone Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+1234567890"
              helperText="Include country code (e.g., +1 for US, +44 for UK)"
              disabled={loading}
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              onClick={handleAddPhoneNumber}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Send />}
              fullWidth
            >
              {loading ? "Sending..." : "Send Verification Code"}
            </Button>
          </Box>
        )}

        {/* Step 1: Verify Code */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Verify Your Phone Number
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We sent a verification code to {phoneNumber}. Please check your
              WhatsApp and enter the code below.
            </Typography>

            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="123456"
              helperText="6-digit code sent to your WhatsApp"
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleVerifyCode}
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <CheckCircle />
                }
                sx={{ flex: 1 }}
              >
                {loading ? "Verifying..." : "Verify Code"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleResendCode}
                disabled={loading}
                startIcon={<Refresh />}
              >
                Resend Code
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Complete */}
        {activeStep === 2 && (
          <Box sx={{ textAlign: "center" }}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Verification Complete!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your WhatsApp number has been successfully verified. You will now
              receive important notifications about your auctions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setActiveStep(0);
                setPhoneNumber("");
                setVerificationCode("");
                loadVerificationStatus();
              }}
            >
              Change Phone Number
            </Button>
          </Box>
        )}

        {/* Messages */}
        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Information Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Why Verify Your WhatsApp Number?
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              🔔 Receive real-time auction notifications
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              📢 Get important system updates
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              💬 Direct communication from our support team
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              🎯 Personalized auction alerts
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default WhatsAppVerification;
